<!--
 * @Description: 文章内容渲染组件
 * 处理预渲染的 HTML，并动态加载地图组件（不阻塞主内容显示）
-->
<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch, nextTick } from 'vue'
import type { MapConfig } from './TravelMap.client.vue'
import { useTableOfContents } from '~/composables/useTableOfContents'

// 懒加载地图组件，不阻塞主内容渲染
const LazyTravelMap = defineAsyncComponent(() => 
  import('./TravelMap.client.vue')
)

// TOC 相关
const { setHeadings, clearHeadings } = useTableOfContents()

const props = defineProps<{
  content: string
}>()

// 文章内容容器 ref
const contentRef = ref<HTMLElement | null>(null)

// 地图配置（从 HTML 中解析）
const mapConfig = ref<MapConfig | null>(null)
const mapReady = ref(false)  // 地图是否准备好

// 处理后的内容（移除地图占位符）
const processedContent = computed(() => {
  // 移除地图占位符 div，因为地图会单独渲染
  return props.content.replace(/<div class="travel-map-container"[^>]*><\/div>/g, '')
})

// 图片查看器状态
const viewerVisible = ref(false)
const viewerImages = ref<Array<{
  ImageId: number
  Key: string
  Title: string | null
  Description: string | null
  BlurHash: string | null
  ExifData: string | null
  CreatedAt: string
}>>([])
const viewerInitialIndex = ref(0)

// 从内容中提取所有图片（用于图片查看器）
const extractImagesFromHtml = (html: string) => {
  const images: typeof viewerImages.value = []
  // 匹配 img 标签
  const imgRegex = /<img[^>]+src="([^"]+)"[^>]*alt="([^"]*)"[^>]*>/g
  let match
  let id = 0

  while ((match = imgRegex.exec(html)) !== null) {
    const url = match[1] ?? ''
    const alt = match[2] ?? ''
    const isAbsoluteUrl = /^https?:\/\//i.test(url)
    const key = isAbsoluteUrl ? url : url.replace(/^https?:\/\/[^/]+\//, '')

    images.push({
      ImageId: id++,
      Key: key,
      Title: alt || null,
      Description: null,
      BlurHash: null,
      ExifData: null,
      CreatedAt: new Date().toISOString()
    })
  }

  return images
}

// 解析地图配置（从预渲染的 HTML 占位符中）
const parseMapConfig = () => {
  // 从 HTML 字符串中解析，而不是从 DOM
  const mapRegex = /<div class="travel-map-container" data-map-config="([^"]+)"><\/div>/
  const match = props.content.match(mapRegex)
  
  if (match && match[1]) {
    try {
      // 解码 HTML 实体
      const decodedConfig = match[1]
        .replace(/&quot;/g, '"')
        .replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
      
      mapConfig.value = JSON.parse(decodedConfig)
    } catch (e) {
      console.error('解析地图配置失败:', e)
      mapConfig.value = null
    }
  } else {
    mapConfig.value = null
  }
}

// 处理图片点击
const handleImageClick = (e: MouseEvent) => {
  const target = e.target as HTMLElement
  if (target.tagName === 'IMG' && target.closest('.travel-image, figure')) {
    const figure = target.closest('.travel-image, figure') as HTMLElement
    const images = extractImagesFromHtml(props.content)
    const src = (target as HTMLImageElement).src
    
    // 找到点击图片的索引
    const index = images.findIndex(img => src.includes(img.Key) || img.Key === src)
    
    if (index >= 0) {
      viewerImages.value = images
      viewerInitialIndex.value = index
      viewerVisible.value = true
    }
  }

  // 处理代码块复制按钮点击
  const btn = target.closest('.copy-code-btn')
  if (btn) {
    e.preventDefault() // 阻止默认行为（防止折叠 details）
    e.stopPropagation() // 阻止冒泡

    const details = btn.closest('details')
    const codeBlock = details?.querySelector('code')
    
    if (codeBlock) {
      const code = codeBlock.textContent || ''
      navigator.clipboard.writeText(code).then(() => {
        // 显示成功图标
        const defaultIcon = btn.querySelector('.copy-icon-default') as HTMLElement
        const successIcon = btn.querySelector('.copy-icon-success') as HTMLElement
        
        if (defaultIcon && successIcon) {
          defaultIcon.style.display = 'none'
          successIcon.style.display = 'block'
          
          setTimeout(() => {
            defaultIcon.style.display = 'block'
            successIcon.style.display = 'none'
          }, 2000)
        }
      }).catch(err => {
        console.error('复制失败:', err)
      })
    }
    return
  }
}

// 关闭图片查看器
const handleViewerClose = () => {
  viewerVisible.value = false
}

// 处理地图标记点击
const handleMarkerClick = (markerId: string) => {
  // 滚动到对应章节
  const section = document.getElementById(markerId)
  if (section) {
    section.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }
}

// 地图加载完成回调
const handleMapReady = () => {
  mapReady.value = true
}

// 从 DOM 中提取标题并设置 TOC
const extractAndSetHeadings = async () => {
  await nextTick() // 等待 v-html 渲染完成
  
  if (!contentRef.value) return
  
  const tocItems: Array<{ id: string; text: string; level: number; isMapSection?: boolean }> = []
  
  // 提取所有有 id 的标题
  const headingElements = contentRef.value.querySelectorAll('h1[id], h2[id], h3[id], h4[id], h5[id], h6[id]')
  
  headingElements.forEach((el) => {
    const level = parseInt(el.tagName.charAt(1), 10)
    const id = el.id
    const text = el.textContent?.trim() || ''
    // 检查是否是地图章节（有 .travel-heading 类）
    const isMapSection = el.classList.contains('travel-heading')
    
    if (id && text) {
      tocItems.push({ id, text, level, isMapSection })
    }
  })
  
  setHeadings(tocItems)
}

onMounted(() => {
  parseMapConfig()
  extractAndSetHeadings()
})

onUnmounted(() => {
  clearHeadings()
})

watch(() => props.content, () => {
  parseMapConfig()
  mapReady.value = false
  extractAndSetHeadings()
}, { immediate: true })
</script>

<template>
  <div class="article-content" :class="{ 'article-content--with-map': mapConfig }">
    <!-- 地图区域（顶部，仅客户端渲染） -->
    <div v-if="mapConfig" class="travel-map-header">
      <!-- 骨架屏：地图加载前显示 -->
      <div v-if="!mapReady" class="map-skeleton">
        <div class="map-skeleton-inner">
          <svg class="map-skeleton-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
            <path d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l5.447 2.724A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
          </svg>
          <span>地图加载中...</span>
        </div>
      </div>
      
      <!-- 地图组件（懒加载） -->
      <ClientOnly>
        <Suspense>
          <LazyTravelMap
            :config="mapConfig"
            height="45vh"
            :sticky="false"
            @marker-click="handleMarkerClick"
            @ready="handleMapReady"
          />
        </Suspense>
      </ClientOnly>
    </div>

    <!-- 预渲染的文章内容（始终立即显示） -->
    <article 
      ref="contentRef"
      v-html="processedContent" 
      class="renderer-container travel-content"
      :class="{ 'travel-content--with-map': mapConfig }"
      @click="handleImageClick"
    ></article>

    <!-- 图片查看器 -->
    <ClientOnly>
      <ImageViewer 
        :images="viewerImages" 
        :initial-index="viewerInitialIndex" 
        :visible="viewerVisible"
        @close="handleViewerClose" 
      />
    </ClientOnly>
  </div>
</template>

<style scoped>
.article-content {
  position: relative;
  width: 100%;
}

.article-content--with-map {
  position: relative;
}

/* 地图在顶部 */
.travel-map-header {
  position: relative;
  width: 100%;
  min-height: 45vh;
  margin-bottom: 0;
}

/* 带地图时的内容区域 */
.travel-content--with-map {
  margin: 0 auto;
  background: var(--bg-color, #fff);
  position: relative;
  z-index: 101;
}

/* 地图骨架屏 */
.map-skeleton {
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, #f5f5f5 0%, #e8e8e8 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
}

html[data-theme="dark"] .map-skeleton {
  background: linear-gradient(135deg, #2a2a2a 0%, #1f1f1f 100%);
}

.map-skeleton-inner {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  color: #999;
}

.map-skeleton-icon {
  width: 48px;
  height: 48px;
  animation: pulse 2s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 0.4; }
  50% { opacity: 1; }
}
</style>
